"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import Button from "./Button";
import {
  useGetItineraryDescriptionDetails,
  useAddItineraryDescriptionDetails,
  useUpdateItineraryDescriptionDetails,
  useItineraryDescriptionStatus,
  useItinearyImageUpload,
  getCityByname,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import RTE from "@/components/RTE";

interface ItineraryDescription {
  id: number;
  image_path: string;
  user_name: string;
  start_city: string;
  destination_city: string;
  start_city_name: string;
  destination_city_name: string;
  title: string;
  description: string;
  created_by: string;
  status: number;
}

const CountryDescriptionMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDescription, setCurrentDescription] =
    useState<ItineraryDescription | null>(null);
  const [descriptions, setDescriptions] = useState<ItineraryDescription[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [richTextContent, setRichTextContent] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  // City search states for start city
  const [startCities, setStartCities] = useState<any[]>([]);
  const [startCityTerm, setStartCityTerm] = useState("");
  const [debouncedStartCityTerm, setDebouncedStartCityTerm] = useState("");
  const [selectedStartCity, setSelectedStartCity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showStartCities, setShowStartCities] = useState(false);

  // City search states for end city
  const [endCities, setEndCities] = useState<any[]>([]);
  const [endCityTerm, setEndCityTerm] = useState("");
  const [debouncedEndCityTerm, setDebouncedEndCityTerm] = useState("");
  const [selectedEndCity, setSelectedEndCity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showEndCities, setShowEndCities] = useState(false);

  // Refs for click outside detection
  const startCityRef = useRef<HTMLDivElement>(null);
  const endCityRef = useRef<HTMLDivElement>(null);

  // API Hooks
  const getItineraryDescriptions = useGetItineraryDescriptionDetails();
  const addItineraryDescription = useAddItineraryDescriptionDetails();
  const updateItineraryDescription = useUpdateItineraryDescriptionDetails();
  const updateStatus = useItineraryDescriptionStatus();
  const imageUploadMutation = useItinearyImageUpload(
    (filePath, url) => {
      const fullImageUrl = constructImageUrl(filePath);
      setUploadedImageUrl(fullImageUrl);
    },
    (error) => {
      alert(error.message || "Failed to upload image. Please try again.");
    }
  );

  const startCitiesQuery = getCityByname(debouncedStartCityTerm);
  const endCitiesQuery = getCityByname(debouncedEndCityTerm);

  const user = useAuth();
  const user_id = user?.user?.id;

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        startCityRef.current &&
        !startCityRef.current.contains(event.target as Node)
      ) {
        setShowStartCities(false);
      }
      if (
        endCityRef.current &&
        !endCityRef.current.contains(event.target as Node)
      ) {
        setShowEndCities(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Utility function to construct proper image URLs
  const constructImageUrl = (imagePath: string) => {
    if (!imagePath) return "";

    const baseUrl = process.env.NEXT_PUBLIC_B2B_URL || "";

    let cleanImagePath = imagePath;
    if (baseUrl.endsWith("/") && imagePath.startsWith("/")) {
      cleanImagePath = imagePath.substring(1);
    } else if (!baseUrl.endsWith("/") && !imagePath.startsWith("/")) {
      cleanImagePath = "/" + imagePath;
    }

    return `${baseUrl}${cleanImagePath}`;
  };

  // Fetch itinerary descriptions on component mount
  useEffect(() => {
    fetchItineraryDescriptions();
  }, []);

  const fetchItineraryDescriptions = () => {
    getItineraryDescriptions.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setDescriptions(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching itinerary descriptions:", error);
        },
      }
    );
  };

  useEffect(() => console.log("description", descriptions), [descriptions]);

  // Debounce search term for start city
  useEffect(() => {
    if (startCityTerm.length >= 3) {
      const handler = setTimeout(() => {
        setDebouncedStartCityTerm(startCityTerm);
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setStartCities([]);
      setDebouncedStartCityTerm("");
      setShowStartCities(false);
    }
  }, [startCityTerm]);

  // Debounce search term for end city
  useEffect(() => {
    if (endCityTerm.length >= 3) {
      const handler = setTimeout(() => {
        setDebouncedEndCityTerm(endCityTerm);
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setEndCities([]);
      setDebouncedEndCityTerm("");
      setShowEndCities(false);
    }
  }, [endCityTerm]);

  // Update start cities when debounced search term changes
  useEffect(() => {
    if (debouncedStartCityTerm && startCitiesQuery.data) {
      const citiesData = startCitiesQuery.data?.data;
      if (citiesData && Array.isArray(citiesData)) {
        setStartCities(citiesData);
        setShowStartCities(true);
      } else {
        setStartCities([]);
        setShowStartCities(false);
      }
    } else {
      setStartCities([]);
      setShowStartCities(false);
    }
  }, [debouncedStartCityTerm, startCitiesQuery.data]);

  // Update end cities when debounced search term changes
  useEffect(() => {
    if (debouncedEndCityTerm && endCitiesQuery.data) {
      const citiesData = endCitiesQuery.data?.data;
      if (citiesData && Array.isArray(citiesData)) {
        setEndCities(citiesData);
        setShowEndCities(true);
      } else {
        setEndCities([]);
        setShowEndCities(false);
      }
    } else {
      setEndCities([]);
      setShowEndCities(false);
    }
  }, [debouncedEndCityTerm, endCitiesQuery.data]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setUploadedImageUrl("");
      setCurrentDescription(null);
      setSelectedStartCity(null);
      setSelectedEndCity(null);
      setStartCityTerm("");
      setEndCityTerm("");
      setStartCities([]);
      setEndCities([]);
      setRichTextContent("");
      setTitle("");
      setShowStartCities(false);
      setShowEndCities(false);
    }
  }, [isModalOpen]);

  // Set form data when currentDescription changes (for editing)
  useEffect(() => {
    if (currentDescription && isModalOpen) {
      setRichTextContent(currentDescription.description || "");
      setTitle(currentDescription.title || "");

      // Reset city terms initially
      setStartCityTerm("");
      setEndCityTerm("");
      setSelectedStartCity(null);
      setSelectedEndCity(null);

      // Use the existing search functionality to find city names
      if (currentDescription.start_city && currentDescription.start_city_name) {
        // Set the ID temporarily, the search will find the name
        setStartCityTerm(currentDescription.start_city);
      }

      if (currentDescription.destination_city) {
        setEndCityTerm(currentDescription.destination_city);
      }
    }
  }, [currentDescription, isModalOpen]);

  // Auto-select cities when search results come back for editing

  // Set form data when currentDescription changes (for editing)
  useEffect(() => {
    if (currentDescription && isModalOpen) {
      setRichTextContent(currentDescription.description || "");
      setTitle(currentDescription.title || "");

      // Reset city terms initially
      setStartCityTerm("");
      setEndCityTerm("");
      setSelectedStartCity(null);
      setSelectedEndCity(null);

      // Use the city names from the API response for display
      if (currentDescription.start_city && currentDescription.start_city_name) {
        setStartCityTerm(currentDescription.start_city_name);
        setSelectedStartCity({
          id: currentDescription.start_city.toString(),
          name: currentDescription.start_city_name,
        });
      }

      if (
        currentDescription.destination_city &&
        currentDescription.destination_city_name
      ) {
        setEndCityTerm(currentDescription.destination_city_name);
        setSelectedEndCity({
          id: currentDescription.destination_city.toString(),
          name: currentDescription.destination_city_name,
        });
      }
    }
  }, [currentDescription, isModalOpen]);

  // useEffect(() => {
  //   if (currentDescription && isModalOpen) {
  //     // Auto-select start city when search results match
  //     if (startCities.length > 0 && currentDescription.start_city && !selectedStartCity) {
  //       const matchingStartCity = startCities.find(
  //         city => city.city_id.toString() === currentDescription.start_city
  //       );
  //       if (matchingStartCity) {
  //         setSelectedStartCity({
  //           id: matchingStartCity.city_id.toString(),
  //           name: matchingStartCity.city_name
  //         });
  //         setStartCityTerm(matchingStartCity.city_name);
  //       }
  //     }

  //     // Auto-select end city when search results match
  //     if (endCities.length > 0 && currentDescription.destination_city && !selectedEndCity) {
  //       const matchingEndCity = endCities.find(
  //         city => city.city_id.toString() === currentDescription.destination_city
  //       );
  //       if (matchingEndCity) {
  //         setSelectedEndCity({
  //           id: matchingEndCity.city_id.toString(),
  //           name: matchingEndCity.city_name
  //         });
  //         setEndCityTerm(matchingEndCity.city_name);
  //       }
  //     }
  //   }
  // }, [startCities, endCities, currentDescription, isModalOpen, selectedStartCity, selectedEndCity]);

  // Handle file upload when file is selected - IMMEDIATE UPLOAD
  const handleFileUpload = async (file: File) => {
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Create temporary preview URL for immediate display
    const tempUrl = URL.createObjectURL(file);
    setUploadedImageUrl(tempUrl);

    // Use the mutation to upload the file immediately
    imageUploadMutation.mutate(file, {
      onSuccess: (data) => {
        // The onSuccess callback in useItinearyImageUpload will handle setting the proper URL
        // Revoke the temporary URL
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
      },
      onError: (error) => {
        // Revoke temporary URL on error
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
        setUploadedImageUrl("");
      },
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate city selections
    if (!selectedStartCity) {
      alert("Please select a start city");
      return;
    }
    if (!selectedEndCity) {
      alert("Please select an end city");
      return;
    }
    if (!title.trim()) {
      alert("Please enter a title");
      return;
    }
    if (!richTextContent.trim()) {
      alert("Please enter a description");
      return;
    }

    const loggedInUser = user?.user?.id;

    try {
      // Extract just the path part from the uploaded image URL if it's a full URL
      let imagePath = "";
      if (uploadedImageUrl) {
        // If it's a newly uploaded image, get just the path part
        if (uploadedImageUrl.startsWith("http")) {
          const url = new URL(uploadedImageUrl);
          imagePath = url.pathname;
        } else {
          // If it's still a blob URL, wait for the actual upload to complete
          if (imageUploadMutation.isPending) {
            alert("Please wait for image upload to complete");
            return;
          }
          // Use the existing image path if available
          imagePath = currentDescription?.image_path || "";
        }
      } else if (currentDescription?.image_path) {
        // If no new upload, use the existing path
        imagePath = currentDescription.image_path;
      }

      const payload = {
        start_city: selectedStartCity.id,
        destination_city: selectedEndCity.id,
        user_id: user_id,
        title: title,
        description: richTextContent,
        image_path: imagePath,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentDescription) {
        // Update existing itinerary description
        updateItineraryDescription.mutate(
          {
            ...payload,
            auto_id: currentDescription.id,
            status: currentDescription.status,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchItineraryDescriptions();
                setIsModalOpen(false);
                setCurrentDescription(null);
                setUploadedImageUrl("");
                setRichTextContent("");
                setTitle("");
              }
            },
            onError: (error) => {
              alert(
                "Failed to update itinerary description. Please try again."
              );
            },
          }
        );
      } else {
        // Add new itinerary description
        addItineraryDescription.mutate(
          {
            ...payload,
            status: 1,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchItineraryDescriptions();
                setIsModalOpen(false);
                setUploadedImageUrl("");
                setRichTextContent("");
                setTitle("");
              }
            },
            onError: (error) => {
              alert("Failed to add itinerary description. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchItineraryDescriptions();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchItineraryDescriptions();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (
      confirm(
        "Are you sure you want to delete selected itinerary descriptions?"
      )
    ) {
      // Note: You might need to implement a delete mutation if available
      // For now, we'll just clear selection
      setSelectedRows([]);
    }
  };

  // Transform API data to match table structure
  const tableData = descriptions.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    image: item.image_path,
    user: item.user_name,
    startCity: item.start_city_name,
    endCity: item.destination_city_name,
    title: item.title,
    description: item.description,
    createdBy: item.created_by,
    status: item.status,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    {
      key: "image",
      header: "Image",
      render: (item: any) => {
        if (!item) {
          return (
            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-xs text-gray-500">No Image</span>
            </div>
          );
        }

        const imageUrl = constructImageUrl(item);

        return (
          <div className="w-12 h-12 rounded overflow-hidden">
            <img
              src={imageUrl}
              alt="User"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'%3E%3C/path%3E%3C/svg%3E";
              }}
            />
          </div>
        );
      },
    },
    { key: "user", header: "User", sortable: true },
    { key: "startCity", header: "Start City", sortable: true },
    { key: "endCity", header: "End City", sortable: true },
    { key: "title", header: "Title", sortable: true },
    {
      key: "description",
      header: "Description",
      sortable: true,
      width: "300px",
      render: (description: string) => (
        <div
          className="max-w-[300px] overflow-hidden text-ellipsis line-clamp-3"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      ),
    },
    { key: "createdBy", header: "Created By", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentDescription(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this itinerary description?"
                  )
                ) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentDescription(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Itinerary Description
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={
            getItineraryDescriptions.isPending || imageUploadMutation.isPending
          }
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentDescription(null);
              setUploadedImageUrl("");
              setRichTextContent("");
              setTitle("");
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-4xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentDescription
                    ? "Update Itinerary Description"
                    : "Add Itinerary Description"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentDescription(null);
                    setUploadedImageUrl("");
                    setRichTextContent("");
                    setTitle("");
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Image Upload */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={imageUploadMutation.isPending}
                    />
                    {imageUploadMutation.isPending && (
                      <p className="text-sm text-blue-600">
                        Uploading image...
                      </p>
                    )}
                    {(uploadedImageUrl || currentDescription?.image_path) && (
                      <div className="mt-2">
                        <img
                          src={
                            uploadedImageUrl ||
                            constructImageUrl(currentDescription!.image_path)
                          }
                          alt="Preview"
                          className="h-32 w-32 object-cover rounded-md border"
                        />
                      </div>
                    )}
                  </div>

                  {/* Start City Search */}
                  <div className="space-y-2" ref={startCityRef}>
                    <label className="block text-sm font-medium text-gray-700">
                      Start City *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={startCityTerm || ""}
                        onChange={(e) => {
                          setStartCityTerm(e.target.value);
                          setShowStartCities(true);
                        }}
                        onFocus={() => {
                          if (startCities.length > 0) {
                            setShowStartCities(true);
                          }
                        }}
                        placeholder="Type at least 3 characters to search for start city..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {startCitiesQuery.isLoading && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                      {showStartCities && startCities.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {startCities.map((city) => (
                            <div
                              key={city.city_id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedStartCity({
                                  id: city.city_id.toString(),
                                  name: city.city_name,
                                });
                                setStartCityTerm(city.city_name);
                                setShowStartCities(false);
                              }}
                            >
                              {city.city_name}, {city.state_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* End City Search */}
                  <div className="space-y-2" ref={endCityRef}>
                    <label className="block text-sm font-medium text-gray-700">
                      End City *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={endCityTerm || ""}
                        onChange={(e) => {
                          setEndCityTerm(e.target.value);
                          setShowEndCities(true);
                        }}
                        onFocus={() => {
                          if (endCities.length > 0) {
                            setShowEndCities(true);
                          }
                        }}
                        placeholder="Type at least 3 characters to search for end city..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {endCitiesQuery.isLoading && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                      {showEndCities && endCities.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {endCities.map((city) => (
                            <div
                              key={city.city_id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedEndCity({
                                  id: city.city_id.toString(),
                                  name: city.city_name,
                                });
                                setEndCityTerm(city.city_name);
                                setShowEndCities(false);
                              }}
                            >
                              {city.city_name}, {city.state_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={title || ""}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter title"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Rich Text Editor for Description */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Description *
                    </label>
                    <RTE
                      content={richTextContent}
                      onChange={setRichTextContent}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addItineraryDescription.isPending ||
                        updateItineraryDescription.isPending ||
                        imageUploadMutation.isPending
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addItineraryDescription.isPending ||
                      updateItineraryDescription.isPending
                        ? "Processing..."
                        : currentDescription
                        ? "Update Itinerary Description"
                        : "Add Itinerary Description"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryDescriptionMaster;
